/**
 * SEO Manager for Multilingual Support
 * Handles dynamic meta tag updates based on language selection
 */

export class SEOManager {
    constructor() {
        this.seoData = {
            en: {
                title: 'Pythagoras Trees - Interactive Fractal Visualization',
                description:
                    'Interactive Pythagoras Tree (Bosman Tree) fractal generator with 10+ color patterns, real-time rendering, and customizable parameters. Explore mathematical beauty through interactive visualization.',
                keywords:
                    'pythagoras tree, bosman tree, fractal generator, mathematical visualization, interactive fractals, geometric patterns, educational math tools, pythagorean tree, bosman fractal',
                structuredData: {
                    name: 'Pythagoras Tree Fractal Visualizer',
                    description:
                        'Interactive web application for generating and visualizing Pythagoras Tree (Bosman Tree) fractals with customizable parameters and color patterns.',
                    keywords:
                        'pythagoras tree, bosman tree, fractal, mathematics, visualization, education, interactive, geometry, pythagorean tree',
                },
            },
            it: {
                title: 'Albero di Pitagora - Visualizzazione Interattiva di Frattali',
                description:
                    'Generatore interattivo di frattali Albero di Pitagora (Albero di Bosman) con oltre 10 pattern di colori, rendering in tempo reale e parametri personalizzabili. Esplora la bellezza matematica attraverso la visualizzazione interattiva.',
                keywords:
                    'albero di pitagora, albero di bosman, generatore frattali, visualizzazione matematica, frattali interattivi, pattern geometrici, strumenti educativi matematici, bosman tree, pythagoras tree',
                structuredData: {
                    name: 'Visualizzatore Frattali Albero di Pitagora',
                    description:
                        'Applicazione web interattiva per generare e visualizzare frattali Albero di Pitagora (Albero di Bosman) con parametri personalizzabili e pattern di colori.',
                    keywords:
                        'albero di pitagora, albero di bosman, frattale, matematica, visualizzazione, educazione, interattivo, geometria',
                },
            },
            es: {
                title: 'Árbol de Pitágoras - Visualización Interactiva de Fractales',
                description:
                    'Generador interactivo de fractales Árbol de Pitágoras (Árbol de Bosman) con más de 10 patrones de colores, renderizado en tiempo real y parámetros personalizables. Explora la belleza matemática a través de la visualización interactiva.',
                keywords:
                    'árbol de pitágoras, árbol de bosman, generador fractales, visualización matemática, fractales interactivos, patrones geométricos, herramientas educativas matemáticas, bosman tree, pythagoras tree',
                structuredData: {
                    name: 'Visualizador de Fractales Árbol de Pitágoras',
                    description:
                        'Aplicación web interactiva para generar y visualizar fractales Árbol de Pitágoras (Árbol de Bosman) con parámetros personalizables y patrones de colores.',
                    keywords:
                        'árbol de pitágoras, árbol de bosman, fractal, matemáticas, visualización, educación, interactivo, geometría',
                },
            },
            fr: {
                title: 'Arbre de Pythagore - Visualisation Interactive de Fractales',
                description:
                    'Générateur interactif de fractales Arbre de Pythagore (Arbre de Bosman) avec plus de 10 motifs de couleurs, rendu en temps réel et paramètres personnalisables. Explorez la beauté mathématique grâce à la visualisation interactive.',
                keywords:
                    'arbre de pythagore, arbre de bosman, générateur fractales, visualisation mathématique, fractales interactives, motifs géométriques, outils éducatifs mathématiques, bosman tree, pythagoras tree',
                structuredData: {
                    name: 'Visualiseur de Fractales Arbre de Pythagore',
                    description:
                        'Application web interactive pour générer et visualiser des fractales Arbre de Pythagore (Arbre de Bosman) avec des paramètres personnalisables et des motifs de couleurs.',
                    keywords:
                        'arbre de pythagore, arbre de bosman, fractale, mathématiques, visualisation, éducation, interactif, géométrie',
                },
            },
            de: {
                title: 'Pythagoras-Baum - Interaktive Fraktal-Visualisierung',
                description:
                    'Interaktiver Pythagoras-Baum (Bosman-Baum) Fraktal-Generator mit über 10 Farbmustern, Echtzeit-Rendering und anpassbaren Parametern. Entdecken Sie mathematische Schönheit durch interaktive Visualisierung.',
                keywords:
                    'pythagoras baum, bosman baum, fraktal generator, mathematische visualisierung, interaktive fraktale, geometrische muster, mathematische bildungstools, bosman tree, pythagoras tree',
                structuredData: {
                    name: 'Pythagoras-Baum Fraktal-Visualisierer',
                    description:
                        'Interaktive Webanwendung zur Generierung und Visualisierung von Pythagoras-Baum (Bosman-Baum) Fraktalen mit anpassbaren Parametern und Farbmustern.',
                    keywords:
                        'pythagoras baum, bosman baum, fraktal, mathematik, visualisierung, bildung, interaktiv, geometrie',
                },
            },
        };
    }

    /**
     * Update page meta tags based on current language
     * @param {string} langCode - Language code (e.g., 'it', 'en')
     */
    updateMetaTags(langCode) {
        const seoData = this.seoData[langCode] || this.seoData.en;

        // Update document title
        document.title = seoData.title;

        // Update meta description
        this.updateMetaTag('name', 'description', seoData.description);

        // Update meta keywords
        this.updateMetaTag('name', 'keywords', seoData.keywords);

        // Update HTML lang attribute
        document.documentElement.lang = langCode;

        // Update structured data
        this.updateStructuredData(langCode);

        // Update Open Graph tags
        this.updateOpenGraphTags(langCode);
    }

    /**
     * Update a specific meta tag
     * @param {string} attribute - Attribute name ('name', 'property', etc.)
     * @param {string} value - Attribute value
     * @param {string} content - Content to set
     */
    updateMetaTag(attribute, value, content) {
        let metaTag = document.querySelector(`meta[${attribute}="${value}"]`);
        if (metaTag) {
            metaTag.setAttribute('content', content);
        } else {
            // Create new meta tag if it doesn't exist
            metaTag = document.createElement('meta');
            metaTag.setAttribute(attribute, value);
            metaTag.setAttribute('content', content);
            document.head.appendChild(metaTag);
        }
    }

    /**
     * Update structured data (JSON-LD)
     * @param {string} langCode - Language code
     */
    updateStructuredData(langCode) {
        const seoData = this.seoData[langCode] || this.seoData.en;

        // Find existing structured data script
        const structuredDataScript = document.querySelector('script[type="application/ld+json"]');

        if (structuredDataScript) {
            try {
                const structuredData = JSON.parse(structuredDataScript.textContent);

                // Update with localized content
                structuredData.name = seoData.structuredData.name;
                structuredData.description = seoData.structuredData.description;
                structuredData.keywords = seoData.structuredData.keywords;
                structuredData.inLanguage = [langCode];

                // Update the script content
                structuredDataScript.textContent = JSON.stringify(structuredData, null, 2);
            } catch (error) {
                console.error('Error updating structured data:', error);
            }
        }
    }

    /**
     * Update Open Graph tags for social media
     * @param {string} langCode - Language code
     */
    updateOpenGraphTags(langCode) {
        const seoData = this.seoData[langCode] || this.seoData.en;

        // Update Open Graph title
        this.updateMetaTag('property', 'og:title', seoData.title);

        // Update Open Graph description
        this.updateMetaTag('property', 'og:description', seoData.description);

        // Update Twitter Card title
        this.updateMetaTag('name', 'twitter:title', seoData.title);

        // Update Twitter Card description
        this.updateMetaTag('name', 'twitter:description', seoData.description);

        // Update page URL with language parameter
        const currentUrl = new URL(window.location.href);
        if (langCode !== 'en') {
            currentUrl.searchParams.set('lang', langCode);
        } else {
            currentUrl.searchParams.delete('lang');
        }

        this.updateMetaTag('property', 'og:url', currentUrl.toString());

        // Update canonical URL
        const canonicalLink = document.querySelector('link[rel="canonical"]');
        if (canonicalLink) {
            canonicalLink.setAttribute('href', currentUrl.toString());
        }

        // Update browser URL without page reload
        try {
            window.history.replaceState({}, '', currentUrl.toString());
        } catch (error) {
            console.warn('Could not update browser URL:', error);
        }
    }

    /**
     * Get SEO data for a specific language
     * @param {string} langCode - Language code
     * @returns {Object} SEO data object
     */
    getSEOData(langCode) {
        return this.seoData[langCode] || this.seoData.en;
    }

    /**
     * Add new language SEO data
     * @param {string} langCode - Language code
     * @param {Object} seoData - SEO data object
     */
    addLanguageSEOData(langCode, seoData) {
        this.seoData[langCode] = seoData;
    }
}

// Create and export singleton instance
export const seoManager = new SEOManager();
